import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {useApiClient} from "@/hooks/api-hook.ts";
import {
  FlightNumber,
  FlightNumberCreateRequest,
  FlightNumberUpdateRequest
} from "@/api/client/models/index.ts";

export const useCharterFlightNumbersStore = defineStore('charter-flight-numbers', () => {
  const api = useApiClient()

    // State
    const flightNumbers = ref<FlightNumber[]>([]);
    const loading = ref(false);
    const error = ref<string | null>(null);

    // Getters
    const flightNumbersCount = computed(() => flightNumbers.value.length);
    const hasFlightNumbers = computed(() => flightNumbers.value.length > 0);
    const isLoading = computed(() => loading.value);
    const hasError = computed(() => error.value !== null);

    // Actions
    async function fetchFlightNumbers(isActive: boolean = true) {
        loading.value = true;
        error.value = null;

        try {
            flightNumbers.value = await api.flightNumber.get({queryParameters: {isActive}})
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Failed to fetch flight numbers';
            console.error('Error fetching flight numbers:', err);
        } finally {
            loading.value = false;
        }
    }

    function clearFlightNumbers() {
        flightNumbers.value = [];
        error.value = null;
    }

    function clearError() {
        error.value = null;
    }

    async function addFlightNumber(flightNumberCreateRequest: FlightNumberCreateRequest) {
        loading.value = true;
        error.value = null;

        try {
            const newFlightNumber = await api.flightNumber.post(flightNumberCreateRequest);
            // Refresh the flight numbers list after adding a new flight number
            await fetchFlightNumbers();
            return newFlightNumber;
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Failed to add flight number';
            console.error('Error adding flight number:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    }

    async function updateFlightNumber(flightNumberUpdateRequest: FlightNumberUpdateRequest) {
        loading.value = true;
        error.value = null;

        try {
            await api.flightNumber.put(flightNumberUpdateRequest)
            // Refresh the flight numbers list after updating a flight number
            await fetchFlightNumbers();
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Failed to update flight number';
            console.error('Error updating flight number:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    }

    async function toggleFlightNumberActive(id: number, isActive: boolean) {
        loading.value = true;
        error.value = null;

        try {
            await api.flightNumber.byId(id).byIsActive(isActive).post()
            // Refresh the flight numbers list after toggling flight number active status
            await fetchFlightNumbers();
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Failed to toggle flight number active status';
            console.error('Error toggling flight number active status:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    }

    return {
        // State
        flightNumbers,
        loading,
        error,

        // Getters
        flightNumbersCount,
        hasFlightNumbers,
        isLoading,
        hasError,

        // Actions
        fetchFlightNumbers,
        clearFlightNumbers,
        clearError,
        addFlightNumber,
        updateFlightNumber,
        toggleFlightNumberActive
    };
});
